import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';

import { organizationStore } from './organizationStore';

/** The organization id this tab has selected, or null for "the account's default". */
export function useSelectedOrganizationId(): string | null {
  return useSyncExternalStore(
    organizationStore.subscribe,
    organizationStore.get,
    organizationStore.get,
  );
}

/**
 * Moves this tab into another organization.
 *
 * <p>Every cached query belongs to the organization it was fetched in, so they are all
 * reset, not invalidated: an invalidation keeps the old rows on screen while the refetch
 * runs, which is the previous tenant's trucks painted under the new tenant's name -- a
 * cross-tenant exposure, however brief. Resetting drops the data, `/users/me` goes back
 * to pending, and the bootstrap gate holds the splash until the new organization is known.
 *
 * <p>Pass null to drop the selection and let the server choose the default again.
 */
export function useSwitchOrganization() {
  const queryClient = useQueryClient();

  return useCallback(
    async (organizationId: string | null) => {
      organizationStore.set(organizationId);
      queryClient.getMutationCache().clear();
      await queryClient.resetQueries();
    },
    [queryClient],
  );
}
